import { AuthService } from '../../singleton-services/auth/auth.service'
import { Inject, Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, CanActivate, Params, Router, RouterStateSnapshot } from '@angular/router'
import { combineLatest, Observable, of } from 'rxjs'
import { tap, switchMap } from 'rxjs/operators'
import { CheckoutNavigationService } from '../services/checkout-navigation.service'
import { VerticalService } from '../../singleton-services/vertical.service'
import { PlatformService } from '../../singleton-services/platform.service'
import { HttpParams } from '@angular/common/http'
import { WINDOW } from '../../app.injections'
import { buildMileSplitJoinURL } from '../services/external-routing.utility'
import { AccountService } from 'src/app/singleton-services/account.service'
import { ExperimentationService } from 'src/app/shared/experimentation/experimentation.service'
import {
  FEAT_WEBFLOW_SIGNUP_REDIRECT_ID,
  FEAT_WEBFLOW_SIGNUP_REDIRECT_VARIATION
} from 'src/app/shared/experimentation/experiment.model'
import { OffersService } from 'src/app/singleton-services/offers.service'
import { OfferSelectionCardModel } from '@flocasts/experience-service-types'
import { isOfferSelectionCardModel } from 'src/app/shared/functions/type-guards'
import { LOGGER_SERVICE } from 'src/app/logger/logger.config'
import { LoggerService } from 'src/app/logger/logger.interface'
import { SegmentService } from 'src/app/shared/analytics/services/segment.service'
import { SiteIds } from 'src/app/shared/models/site.model'

export function buildMileSplitRedirectURL(redirect: string): string {
  let params = new HttpParams()
  params = params.set('next', `https://accounts.milesplit.com${redirect}`)

  if (redirect.length > 0) {
    return `https://www.milesplit.com/join?${params.toString()}`
  }
  return 'https://www.milesplit.com/join'
}

@Injectable()
export class LoginSignupGuard implements CanActivate {
  constructor(
    private readonly authService: AuthService,
    private readonly vs: VerticalService,
    private readonly experimentService: ExperimentationService,
    private readonly checkoutNavService: CheckoutNavigationService,
    private readonly ps: PlatformService,
    private readonly router: Router,
    private accountService: AccountService,
    private readonly offersService: OffersService,
    private readonly segmentService: SegmentService,
    @Inject(WINDOW) private readonly window: Window,
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService
  ) {}

  private redirectMileSplitUsers(nextPath?: string, redirect?: string): void {
    const baseURL = nextPath ? buildMileSplitJoinURL(nextPath.toLowerCase()) : 'https://www.milesplit.com/join'

    const destination = redirect ? buildMileSplitRedirectURL(redirect) : buildMileSplitJoinURL(baseURL)

    if (this.ps.isBrowser) {
      this.window.location.href = destination
    } else {
      this.router.navigateByUrl(destination)
    }
  }

  canActivate(ars: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // TODO: FLO-10749 -- Look into removing isMileSplit$ from this logic
    return combineLatest([
      this.authService.userIdentity$,
      this.vs.siteId$,
      this.authService.isPremium$,
      this.accountService.activeSubscription$,
      this.experimentService.isInVariation(
        FEAT_WEBFLOW_SIGNUP_REDIRECT_ID,
        FEAT_WEBFLOW_SIGNUP_REDIRECT_VARIATION,
        state.url.includes('signup') ?? false
      )
    ]).pipe(
      switchMap(([user, siteId, isPremium, activeSubscription, isWebflowFeature]) => {
        const queryParams: Params = ars.queryParams
        const nextPath: string | undefined = queryParams && queryParams.next
        const redirect: string | undefined = queryParams && queryParams.redirect
        const selectedOffer: string | undefined = queryParams && queryParams.selectedOffer

        /** Verticals that are fully rolled out on Webflow */
        const webflowSiteIds = [SiteIds.FLOHOCKEY, SiteIds.FLOGRAPPLING]
        const allowWebflowRedirects = isWebflowFeature || webflowSiteIds.includes(siteId)

        // check if the query param has selectedOffer, direct to create account with a redirect to skip plans
        // used from webflow when a plan is selected
        if (selectedOffer) {
          let offer: OfferSelectionCardModel
          try {
            offer = JSON.parse(selectedOffer)
            if (isOfferSelectionCardModel(offer)) {
              this.offersService.updateSelectedOffer(offer)
              return of(false).pipe(
                tap(() => this.checkoutNavService.navigateAfterLandingPage({ redirect: '/pay' }, false))
              )
            } else {
              this.logger.error(`selectedOffer is not of type OfferSelectionModel. selectedOffer=${selectedOffer}`)
            }
          } catch (err) {
            this.logger.error(`Error parsing selectedOffer: ${selectedOffer}`, err)
          }
        }

        if (!user) {
          if (state.url.startsWith('/signup')) {
            if (siteId === SiteIds.MILESPLIT) {
              return of(false).pipe(
                tap(() => {
                  this.redirectMileSplitUsers(nextPath, redirect)
                })
              )
            }

            // check if the webflow redirect feature is enabled and if the query param is not present
            if (!queryParams?.w && !redirect?.includes('live') && allowWebflowRedirects) {
              return of(false).pipe(
                tap(() => {
                  const url = this.router
                    .createUrlTree(['/signup'], {
                      queryParams: { ...queryParams, w: 1, anonymousId: this.segmentService.anonymousId },
                      queryParamsHandling: 'merge'
                    })
                    .toString()

                  this.window.location.href = url
                })
              )
            }
          }

          if (siteId === SiteIds.TRACK_WRESTLING && state.url.includes('login')) {
            return of(false).pipe(
              tap(() => {
                this.checkoutNavService.redirectTrackWrestlingUsersForLogin()
              })
            )
          }

          return of(true)
        }

        if (isPremium) {
          return of(false).pipe(tap(() => this.checkoutNavService.navigateToFinalDestination(queryParams)))
        }

        if (!isPremium) {
          // Allow free users in new funnel experience to see the benefits of signing up
          if (state.url.startsWith('/signup')) {
            // check if the webflow redirect feature is enabled and if the query param is not present
            if (!queryParams?.w && !redirect?.includes('live') && allowWebflowRedirects) {
              return of(false).pipe(
                tap(() => {
                  const url = this.router
                    .createUrlTree(['/signup'], {
                      queryParams: { ...queryParams, w: 1, anonymousId: this.segmentService.anonymousId },
                      queryParamsHandling: 'merge'
                    })
                    .toString()

                  this.window.location.href = url
                })
              )
            }

            if (siteId !== SiteIds.MILESPLIT) {
              return of(true)
            }

            return of(false).pipe(
              tap(() => {
                this.redirectMileSplitUsers(nextPath, redirect)
              })
            )
          }

          return of(false).pipe(
            tap(() =>
              this.checkoutNavService.navigateAfterLogin(!!user, !activeSubscription, queryParams, ars.data.liveEvent)
            )
          )
        }

        return of(true)
      })
    )
  }
}
